import React from 'react';
import { FileText, Loader2 } from 'lucide-react';
import BackendDetailsPopup from '../components/BackendDetailsPopup';
import PAAContentActionModal from '../components/PAAContentActionModal';
import { API_ENDPOINTS } from '../config/api';
import { useBrand } from '../context/BrandContext';
import { getBrandStorageItem, getBrandStorageKey } from '../utils/brandStorage';

type RowStatus = 'idle' | 'running' | 'completed' | 'error';

/**
 * FAQ Q&A pair structure
 * Why this matters: Stores individual question-answer pairs for content generation
 */
interface FAQPair {
  question: string;
  answer: string;
}

/**
 * Tree row structure - one row per tree
 * Why this matters: Each row represents a single tree with ALL its FAQ Q&A pairs
 */
interface TreeRow {
  id: string;
  treeName: string;
  treeSlug: string;
  questionCount: number;
  faqPairs: FAQPair[]; // All Q&A pairs for this tree
  status: RowStatus;
  output?: string | undefined; // Generated comprehensive FAQ article
  seoTitle?: string | undefined;
  seoDescription?: string | undefined;
  progressInfo?: string | undefined;
  metadata?: {
    seoTitle: string;
    seoDescription: string;
    word_count: number;
    seo_optimized: boolean;
    aeo_optimized: boolean;
    product_links_included: boolean;
    blog_link_included: boolean;
  } | undefined;
  workflowDetails?: {
    contentGeneration?: {
      processing_steps: string[];
      model_pipeline: string[];
    };
    currentStage?: string;
  } | undefined;
  createdAt: string;
}

/**
 * Convert slug to display name
 * Why this matters: Transforms "meyer-lemon-tree" to "Meyer Lemon Tree" for user-friendly display
 */
function slugToDisplayName(slug: string): string {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * PeopleAlsoAskPage - Tree-based FAQ Content Generator
 * Why this matters: Shows one row per tree, clicking "Run" generates a comprehensive
 * FAQ article covering ALL questions for that tree
 */
const PeopleAlsoAskPage: React.FC = () => {
  const { brand, brandKey } = useBrand();
  const rowsStorageKey = getBrandStorageKey(brandKey, 'paa_trees', { version: 'v3' });
  // List of all tree slugs (40 trees)
  const treeSlugs = [
    'australian-finger-lime-tree',
    'calamondin-calamansi-tree',
    'cara-cara-navel-orange-tree',
    'chandler-pomelo-tree',
    'cleopatra-mandarin-tree',
    'dancy-mandarin-tree',
    'dobashi-beni-satsuma-tree',
    'duncan-grapefruit-tree',
    'etrog-citron-tree',
    'eureka-lemon-tree',
    'eustis-limequat-tree',
    'frost-owari-satsuma-tree',
    'fukushu-kumquat-tree',
    'hirado-buntan-pomelo-tree',
    'kaffir-makrut-lime-tree',
    'kishu-mandarin-tree',
    'marrs-orange-tree',
    'meiwa-kumquat-tree',
    'meyer-lemon-tree',
    'moro-blood-orange-tree',
    'murcott-afourer-mandarin-tree',
    'n-33-navel-orange-tree',
    'nagami-kumquat-tree',
    'new-zealand-lemonade-tree',
    'nippon-orangequat-tree',
    'oro-blanco-grapefruit-tree',
    'page-mandarin-tree',
    'palestine-sweet-lime-tree',
    'persian-lime-tree',
    'pineapple-orange-tree',
    'pink-variegated-lemon-tree',
    'ponkan-mandarin-tree',
    'rangpur-lime-tree',
    'rio-red-grapefruit-tree',
    'sanguinelli-blood-orange-tree',
    'shiranui-dekopon-mandarin-tree',
    'tarocco-blood-orange-tree',
    'thornless-mexican-key-lime-tree',
    'valencia-orange-tree',
    'washington-navel-orange-tree',
    'yuzu-tree'
  ];

  const [rows, setRows] = React.useState<TreeRow[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [selectedRows, setSelectedRows] = React.useState<Set<string>>(new Set());
  const [popupState, setPopupState] = React.useState<{ isVisible: boolean; rowId: string | null; position: { x: number; y: number } }>({ isVisible: false, rowId: null, position: { x: 0, y: 0 } });
  const [isActionModalOpen, setIsActionModalOpen] = React.useState<boolean>(false);
  const [activeModalRowId, setActiveModalRowId] = React.useState<string | null>(null);
  const [runningJobs, setRunningJobs] = React.useState<Map<string, string>>(new Map());
  const activePollingRef = React.useRef<Set<string>>(new Set());
  const abortControllerRef = React.useRef<AbortController | null>(null);
  
  // Use ref to access rows without making it a dependency
  // Why this matters: Allows resumeJobPolling to access current rows without being a useEffect dependency
  const rowsRef = React.useRef<TreeRow[]>(rows);
  
  // Track which jobs we've already attempted to resume this session
  // Why this matters: Prevents repeated resume attempts when runningJobs state updates
  const hasAttemptedResumeRef = React.useRef<Set<string>>(new Set());
  
  // Sequential execution controls
  const stopSequentialRef = React.useRef<boolean>(false);
  const [isSequentialRunning, setIsSequentialRunning] = React.useState<boolean>(false);
  const [sequentialRemaining, setSequentialRemaining] = React.useState<string[]>([]);

  /**
   * Load FAQ markdown file and parse Q&A pairs
   * Why this matters: Extracts ALL question-answer pairs from the markdown file
   */
  const loadFAQPairs = async (treeSlug: string): Promise<FAQPair[]> => {
    try {
      const faqUrl = `${brand.contentPaths.faqBasePath}/${treeSlug}-faqs.md`;
      const response = await fetch(faqUrl);
      if (!response.ok) {
        console.warn(`⚠️ FAQ file not found: ${faqUrl}`);
        return [];
      }

      const markdown = await response.text();
      const pairs: FAQPair[] = [];

      // Parse markdown format: ## N. Question\n\n**Answer:** ...
      const questionPattern = /^## \d+\.\s+(.+)$/gm;
      const answerPattern = /\*\*Answer:\*\*\s*([\s\S]+?)(?=\n---|\n##|$)/gm;

      let match;
      const questions: string[] = [];
      while ((match = questionPattern.exec(markdown)) !== null) {
        questions.push(match[1].trim());
      }

      const answers: string[] = [];
      while ((match = answerPattern.exec(markdown)) !== null) {
        answers.push(match[1].trim());
      }

      // Pair questions with answers
      for (let i = 0; i < Math.min(questions.length, answers.length); i++) {
        pairs.push({
          question: questions[i],
          answer: answers[i]
        });
      }

      return pairs;
    } catch (err: any) {
      console.error(`❌ Failed to load FAQ pairs for ${treeSlug}:`, err);
      return [];
    }
  };

  /**
   * Initialize all tree rows on mount
   * Why this matters: Loads FAQ data for all trees and creates one row per tree
   * Also restores runningJobs to resume polling after page refresh
   */
  React.useEffect(() => {
    const initializeRows = async () => {
      setIsLoading(true);
      
      // Try to restore from localStorage first
      try {
        const saved = getBrandStorageItem(brandKey, 'paa_trees', {
          version: 'v3',
          legacyKeys: ['us_citrus_ai_paa_trees_v3'],
        });
        if (saved) {
          const parsed = JSON.parse(saved);
          
          // Handle both old format (array) and new format (object with rows and runningJobs)
          if (Array.isArray(parsed) && parsed.length > 0) {
            // Old format - just rows array
            setRows(parsed);
            setIsLoading(false);
            console.log(`🔄 Restored ${parsed.length} tree rows from localStorage (old format)`);
            return;
          } else if (parsed && Array.isArray(parsed.rows) && parsed.rows.length > 0) {
            // New format - object with rows and runningJobs
            setRows(parsed.rows);
            
            // Restore runningJobs if present
            if (parsed.runningJobs && typeof parsed.runningJobs === 'object') {
              const jobsMap = new Map(Object.entries(parsed.runningJobs) as [string, string][]);
              setRunningJobs(jobsMap);
              console.log(`🔄 Restored ${jobsMap.size} running jobs for recovery`);
            }
            
            setIsLoading(false);
            console.log(`🔄 Restored ${parsed.rows.length} tree rows from localStorage`);
            return;
          }
        }
      } catch (err) {
        console.warn('Failed to restore from localStorage:', err);
      }

      // Load fresh data for all trees
      const treeRows: TreeRow[] = [];
      
      for (const slug of treeSlugs) {
        const faqPairs = await loadFAQPairs(slug);
        treeRows.push({
          id: slug,
          treeName: slugToDisplayName(slug),
          treeSlug: slug,
          questionCount: faqPairs.length,
          faqPairs,
          status: 'idle',
          createdAt: new Date().toISOString()
        });
      }

      setRows(treeRows);
      setIsLoading(false);
      console.log(`✅ Loaded ${treeRows.length} trees with FAQ data`);
    };

    initializeRows();
  }, [brand.contentPaths.faqBasePath, brandKey]);

  // Keep rowsRef in sync with rows state
  // Why this matters: Allows resumeJobPolling to access current rows without being a useEffect dependency
  React.useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  /**
   * Auto-save progress
   * Why this matters: Persists state changes so reloads return to the same view
   * Now also saves runningJobs so polling can resume after refresh
   */
  React.useEffect(() => {
    if (isLoading || rows.length === 0) return;
    
    const timeoutId = setTimeout(() => {
      try {
        // Save full rows with complete content and runningJobs (no truncation)
        // Why this matters: Preserves full content on page reload, preventing truncation in the modal
        // Also saves runningJobs so polling can resume after refresh
        const dataToSave = {
          rows,
          runningJobs: Object.fromEntries(runningJobs) // Convert Map to Object for JSON serialization
        };
        localStorage.setItem(rowsStorageKey, JSON.stringify(dataToSave));
        console.log(`💾 Saved ${rows.length} tree rows with ${runningJobs.size} running jobs`);
      } catch (err) {
        console.warn('Failed to save progress:', err);
        // If localStorage quota is exceeded, try saving without output fields as fallback
        if (err instanceof DOMException && err.code === 22) {
          console.warn('⚠️ localStorage quota exceeded, attempting to save without output fields');
          try {
            const lightweightRows = rows.map(row => ({
              ...row,
              output: undefined // Remove output to save space, but don't truncate
            }));
            const dataToSave = {
              rows: lightweightRows,
              runningJobs: Object.fromEntries(runningJobs)
            };
            localStorage.setItem(rowsStorageKey, JSON.stringify(dataToSave));
            console.log(`💾 Saved ${rows.length} tree rows without output fields`);
          } catch (fallbackErr) {
            console.error('Failed to save even without output fields:', fallbackErr);
          }
        }
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [isLoading, rows, rowsStorageKey, runningJobs]);

  /**
   * Execute content generation for a tree
   * Why this matters: Generates a comprehensive FAQ article covering ALL questions for the tree
   */
  const executeRow = async (rowId: string) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) {
      console.error(`🚨 Row ${rowId} not found`);
      return;
    }

    if (row.faqPairs.length === 0) {
      console.error(`🚨 No FAQ pairs found for ${row.treeName}`);
      setRows(prev => prev.map(r => 
        r.id === rowId 
          ? { ...r, status: 'error', output: 'No FAQ data available for this tree.' }
          : r
      ));
      return;
    }

    console.log(`🚀 Starting comprehensive FAQ generation for: ${row.treeName} (${row.questionCount} questions)`);

    setRows(prev => prev.map(r => (r.id === rowId ? { ...r, status: 'running', progressInfo: 'Starting...' } : r)));

    let jobId: string | null = null;

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Load brand kit
      let brandKit = null;
      try {
        const savedBrandKit = getBrandStorageItem(brandKey, 'brand_kit', {
          legacyKeys: ['us_citrus_ai_brand_kit', 'us_citrus_brand_kit'],
        });
        if (savedBrandKit) {
          brandKit = JSON.parse(savedBrandKit);
        }
      } catch (e) {
        console.warn('Failed to load brand kit:', e);
      }

      // Generate URLs
      const productUrl = `${brand.quizProductBaseUrl}/${row.treeSlug}`;
      const blogUrl = `${brand.blogBaseUrl}/${row.treeSlug}`;

      // Build request payload with ALL FAQ pairs
      const requestPayload = {
        treeName: row.treeName,
        treeSlug: row.treeSlug,
        faqPairs: row.faqPairs, // Send ALL Q&A pairs
        productUrl,
        blogUrl,
        brand_kit: brandKit
      };

      console.log('🔄 Starting comprehensive FAQ generation:', requestPayload);

      const asyncResp = await fetch(API_ENDPOINTS.paaContentGenerateAsync, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload),
        signal: controller.signal
      });

      if (!asyncResp.ok) {
        const errorText = await asyncResp.text();
        throw new Error(`HTTP ${asyncResp.status}: ${errorText || 'Failed to start content generation'}`);
      }

      const asyncData = await asyncResp.json();
      if (!asyncData.success || !asyncData.jobId) {
        throw new Error(asyncData.error || 'Failed to start content generation');
      }

      jobId = asyncData.jobId;
      if (!jobId) {
        throw new Error('No job ID returned from server');
      }
      console.log(`📋 Started async job ${jobId} for ${row.treeName}`);

      const currentJobId = jobId; // TypeScript now knows this is string, not null

      setRunningJobs(prev => {
        const newMap = new Map(prev);
        newMap.set(rowId, currentJobId);
        return newMap;
      });

      activePollingRef.current.add(currentJobId);

      // Poll for job completion
      let attempts = 0;
      const maxAttempts = 600; // 15 minutes
      const pollInterval = 1500;

      while (attempts < maxAttempts) {
        if (controller.signal.aborted) {
          throw new Error('Operation cancelled');
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));

        try {
          const statusResp = await fetch(API_ENDPOINTS.paaContentJobStatus(currentJobId), {
            signal: controller.signal
          });

          if (!statusResp.ok) {
            attempts++;
            continue;
          }

          const statusData = await statusResp.json();
          const jobData = statusData.job || statusData.data || statusData;

          // Update progress
          if (jobData.progress !== undefined && jobData.status !== 'completed') {
            const progressText = jobData.message || 'Processing...';
            setRows(prev => prev.map(r =>
              r.id === rowId
                ? { ...r, progressInfo: progressText }
                : r
            ));
          }

          if (jobData.status === 'completed') {
            console.log(`✅ Job ${currentJobId} COMPLETED for ${row.treeName}`);

            const resultPayload = jobData.result || jobData.data || jobData;
            const finalContent = resultPayload?.content || resultPayload?.output || '';
            const metadata = resultPayload?.metadata || {};

            const updatedRow: TreeRow = {
              ...row,
              status: 'completed' as const,
              output: finalContent || 'Content generation completed but no content was returned.',
              seoTitle: metadata.seoTitle || `${row.treeName}: Frequently Asked Questions & Answers`,
              seoDescription: metadata.seoDescription || `Find answers to common questions about ${row.treeName}.`,
              metadata: {
                seoTitle: metadata.seoTitle || `${row.treeName}: Frequently Asked Questions & Answers`,
                seoDescription: metadata.seoDescription || `Find answers to common questions about ${row.treeName}.`,
                word_count: metadata.word_count || finalContent.split(/\s+/).length,
                seo_optimized: true,
                aeo_optimized: true,
                product_links_included: metadata.product_links_included || false,
                blog_link_included: metadata.blog_link_included || false
              },
              workflowDetails: {
                contentGeneration: {
                  processing_steps: resultPayload?.generation_metadata?.processing_steps || [],
                  model_pipeline: resultPayload?.generation_metadata?.model_pipeline || []
                }
              },
              progressInfo: ''
            };

            setRows(prev => prev.map(r => (r.id === rowId ? updatedRow : r)));
            setRunningJobs(prev => { const m = new Map(prev); m.delete(rowId); return m; });
            activePollingRef.current.delete(currentJobId);
            return;
          } else if (jobData.status === 'error') {
            throw new Error(jobData.error || 'Content generation failed');
          }
        } catch (pollErr: any) {
          if (pollErr.message === 'Operation cancelled') throw pollErr;
          attempts++;
          continue;
        }

        attempts++;
      }

      throw new Error('Content generation timed out after 15 minutes');
    } catch (err: any) {
      console.error(`❌ Error for ${row.treeName}:`, err?.message || err);

      setRows(prev => prev.map(r =>
        r.id === rowId
          ? { ...r, status: 'error' as const, output: `Error: ${err?.message || 'Generation failed'}`, progressInfo: '' }
          : r
      ));

      setRunningJobs(prev => { const m = new Map(prev); m.delete(rowId); return m; });
      if (jobId) activePollingRef.current.delete(jobId);
    } finally {
      abortControllerRef.current = null;
    }
  };

  /**
   * resumeJobPolling
   * Why this matters: Resumes polling for a specific job after page reload,
   * using the same polling logic as executeRow but without starting a new job.
   * Uses rowsRef.current to avoid stale closure issues during long-running polls.
   */
  const resumeJobPolling = async (jobId: string, rowId: string) => {
    // Use ref to get current row state (avoids stale closures during polling)
    const row = rowsRef.current.find(r => r.id === rowId);
    if (!row) return;

    // Check if we're already polling this job to prevent duplicates
    if (activePollingRef.current.has(jobId)) {
      console.log(`⚠️ [PeopleAlsoAsk] Job ${jobId} already being polled, skipping duplicate`);
      return;
    }

    // Add to active polling
    activePollingRef.current.add(jobId);

    console.log(`🔄 [PeopleAlsoAsk] Resuming job ${jobId} for ${row.treeName}`);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      // Poll for job completion (same logic as executeRow)
      let attempts = 0;
      const maxAttempts = 600; // 15 minutes
      const pollInterval = 1500;

      while (attempts < maxAttempts) {
        if (controller.signal.aborted) {
          throw new Error('Operation cancelled');
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval));

        try {
          const statusResp = await fetch(API_ENDPOINTS.paaContentJobStatus(jobId), {
            signal: controller.signal
          });

          if (!statusResp.ok) {
            attempts++;
            continue;
          }

          const statusData = await statusResp.json();
          const jobData = statusData.job || statusData.data || statusData;

          // Update progress
          if (jobData.progress !== undefined && jobData.status !== 'completed') {
            const progressText = jobData.message || 'Processing...';
            setRows(prev => prev.map(r =>
              r.id === rowId
                ? { ...r, progressInfo: progressText }
                : r
            ));
          }

          if (jobData.status === 'completed') {
            console.log(`✅ Job ${jobId} COMPLETED for ${row.treeName}`);

            const resultPayload = jobData.result || jobData.data || jobData;
            const finalContent = resultPayload?.content || resultPayload?.output || '';
            const metadata = resultPayload?.metadata || {};

            const updatedRow: TreeRow = {
              ...row,
              status: 'completed' as const,
              output: finalContent || 'Content generation completed but no content was returned.',
              seoTitle: metadata.seoTitle || `${row.treeName}: Frequently Asked Questions & Answers`,
              seoDescription: metadata.seoDescription || `Find answers to common questions about ${row.treeName}.`,
              metadata: {
                seoTitle: metadata.seoTitle || `${row.treeName}: Frequently Asked Questions & Answers`,
                seoDescription: metadata.seoDescription || `Find answers to common questions about ${row.treeName}.`,
                word_count: metadata.word_count || finalContent.split(/\s+/).length,
                seo_optimized: true,
                aeo_optimized: true,
                product_links_included: metadata.product_links_included || false,
                blog_link_included: metadata.blog_link_included || false
              },
              workflowDetails: {
                contentGeneration: {
                  processing_steps: resultPayload?.generation_metadata?.processing_steps || [],
                  model_pipeline: resultPayload?.generation_metadata?.model_pipeline || []
                }
              },
              progressInfo: ''
            };

            setRows(prev => prev.map(r => (r.id === rowId ? updatedRow : r)));
            setRunningJobs(prev => { const m = new Map(prev); m.delete(rowId); return m; });
            activePollingRef.current.delete(jobId);
            return;
          } else if (jobData.status === 'error') {
            throw new Error(jobData.error || 'Content generation failed');
          }
        } catch (pollErr: any) {
          if (pollErr.message === 'Operation cancelled') throw pollErr;
          attempts++;
          continue;
        }

        attempts++;
      }

      throw new Error('Content generation timed out after 15 minutes');
    } catch (err: any) {
      console.error(`❌ Error resuming job ${jobId} for ${row.treeName}:`, err?.message || err);

      setRows(prev => prev.map(r =>
        r.id === rowId
          ? { ...r, status: 'error' as const, output: `Error: ${err?.message || 'Generation failed'}`, progressInfo: '' }
          : r
      ));

      setRunningJobs(prev => { const m = new Map(prev); m.delete(rowId); return m; });
      activePollingRef.current.delete(jobId);
    } finally {
      abortControllerRef.current = null;
    }
  };

  /**
   * resumeRunningJobs
   * Why this matters: After page reload, resume polling for any jobs that were running
   * when the user left the page, ensuring content generation continues seamlessly.
   * Includes throttling to prevent resource exhaustion.
   * Uses rowsRef to avoid re-triggering on every poll progress update.
   */
  React.useEffect(() => {
    // Use ref to access rows without making it a dependency
    const currentRows = rowsRef.current;
    if (runningJobs.size === 0 || currentRows.length === 0) return;

    // Filter to only jobs we haven't already attempted to resume
    const jobsToResume: Array<[string, string]> = [];
    runningJobs.forEach((jobId, rowId) => {
      if (!hasAttemptedResumeRef.current.has(jobId)) {
        jobsToResume.push([rowId, jobId]);
      }
    });

    if (jobsToResume.length === 0) return; // All jobs already being handled

    console.log(`🔄 [PeopleAlsoAsk] Resuming polling for ${jobsToResume.length} new jobs with throttling`);
    
    // Resume polling for each running job with staggered delays
    let delayIndex = 0;
    jobsToResume.forEach(([rowId, jobId]) => {
      // Mark as attempted immediately to prevent duplicate resume attempts
      hasAttemptedResumeRef.current.add(jobId);
      
      const row = currentRows.find(r => r.id === rowId);
      if (!row) {
        console.warn(`⚠️ [PeopleAlsoAsk] Row ${rowId} not found for job ${jobId}, removing from running jobs`);
        setRunningJobs(prev => {
          const newMap = new Map(prev);
          newMap.delete(rowId);
          return newMap;
        });
        return;
      }

      // Only resume if row is still in running state
      if (row.status !== 'running') {
        console.log(`📋 [PeopleAlsoAsk] Row ${rowId} no longer running (${row.status}), removing job ${jobId}`);
        setRunningJobs(prev => {
          const newMap = new Map(prev);
          newMap.delete(rowId);
          return newMap;
        });
        return;
      }

      console.log(`🔄 [PeopleAlsoAsk] Resuming job ${jobId} for row ${rowId} (${row.treeName}) with ${delayIndex * 1000}ms delay`);
      
      // Stagger resume requests to prevent resource exhaustion
      setTimeout(() => {
        resumeJobPolling(jobId, rowId);
      }, delayIndex * 1000); // 1 second between each resume
      
      delayIndex++;
    });
  }, [runningJobs]); // Only depend on runningJobs - use rowsRef.current to avoid polling loop spam

  const retryRow = (rowId: string) => {
    setRows(prev => prev.map(r =>
      r.id === rowId
        ? { ...r, status: 'idle' as const, output: '', progressInfo: '' }
        : r
    ));
    executeRow(rowId);
  };

  const toggleRowSelection = (rowId: string) => {
    setSelectedRows(prev => {
      const copy = new Set(prev);
      if (copy.has(rowId)) copy.delete(rowId);
      else copy.add(rowId);
      return copy;
    });
  };

  const selectAll = () => {
    const selectableRows = rows.filter(r => r.status !== 'completed' && r.status !== 'running');
    setSelectedRows(new Set(selectableRows.map(r => r.id)));
  };

  const clearSelection = () => setSelectedRows(new Set());

  /**
   * runBulkExecutionSequential
   * Why this matters: Runs selected rows one-by-one in order (top to bottom) so we don't overwhelm the backend
   */
  const runBulkExecutionSequential = async (targetRowIds?: string[]) => {
    // Get initial queue of eligible rows
    const eligibleRowIds = (targetRowIds && targetRowIds.length > 0 ? targetRowIds : rows.map(r => r.id)).filter(id => {
      const row = rows.find(r => r.id === id);
      return row && row.status !== 'running' && row.status !== 'completed';
    });
    
    // Sort queue by row position (top to bottom) for predictable execution order
    const queue = eligibleRowIds.sort((a, b) => {
      const indexA = rows.findIndex(r => r.id === a);
      const indexB = rows.findIndex(r => r.id === b);
      return indexA - indexB;
    });
    
    if (queue.length === 0) return;
    
    setIsSequentialRunning(true);
    stopSequentialRef.current = false;
    setSequentialRemaining(queue.slice());

    for (let i = 0; i < queue.length; i++) {
      const id = queue[i];
      
      if (stopSequentialRef.current) {
        // Reset any queued rows back to idle
        setRows(prev => prev.map(r => {
          if (r.id === id) {
            return r.status === 'running' ? { ...r, status: 'idle' as const } : r;
          }
          return r;
        }));
        
        // Save the remaining queue
        const remaining = queue.slice(i);
        setSequentialRemaining(remaining);
        setIsSequentialRunning(false);
        return;
      }
      
      setRows(prev => prev.map(r => (r.id === id ? { ...r, status: 'running' as const } : r)));
      
      try {
        await executeRow(id);
      } catch (err: any) {
        if (stopSequentialRef.current || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
          setRows(prev => prev.map(r => {
            if (r.id === id) {
              return { ...r, status: 'idle' as const };
            }
            return r;
          }));
          
          const remaining = queue.slice(i);
          setSequentialRemaining(remaining);
          setIsSequentialRunning(false);
          return;
        }
        console.error(`Error executing row ${id}:`, err);
      }
      
      // Update remaining count
      setSequentialRemaining(queue.slice(i + 1));
    }
    
    setIsSequentialRunning(false);
    setSequentialRemaining([]);
  };

  const anyRowRunning = React.useMemo(() => rows.some(row => row.status === 'running'), [rows]);

  const openActionModal = (rowId: string) => {
    setActiveModalRowId(rowId);
    setIsActionModalOpen(true);
  };

  const closeActionModal = () => {
    setIsActionModalOpen(false);
    setActiveModalRowId(null);
  };

  const handleModalContentUpdate = (rowId: string, newContent: string) => {
    setRows(prev => prev.map(r => (r.id === rowId ? { ...r, output: newContent } : r)));
  };

  const openDetailsPopup = (e: React.MouseEvent, rowId: string) => {
    setPopupState({ isVisible: true, rowId, position: { x: e.clientX, y: e.clientY } });
  };

  const closeDetailsPopup = () => setPopupState(prev => ({ ...prev, isVisible: false, rowId: null }));

  // Stats
  const completedCount = rows.filter(r => r.status === 'completed').length;
  const totalQuestions = rows.reduce((sum, r) => sum + r.questionCount, 0);

  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={{ minHeight: '100vh', backgroundColor: '#ffffff', padding: '2rem' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
            People Also Ask Agent
          </h1>
          <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Generates comprehensive AEO-optimized answers to Google's "People Also Ask" question to boost search and LLM visibility.
          </p>
        </div>

        {/* Stats Bar */}
        <div style={{ 
          display: 'flex', 
          gap: '1.5rem', 
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f9fafb',
          borderRadius: '0.5rem',
          border: '1px solid #e5e7eb'
        }}>
          <div>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Trees</span>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>{rows.length}</div>
          </div>
          <div>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Total Questions</span>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>{totalQuestions}</div>
          </div>
          <div>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Completed</span>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#10b981' }}>{completedCount}</div>
          </div>
          <div>
            <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Remaining</span>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#f59e0b' }}>{rows.length - completedCount}</div>
          </div>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
          <button
            onClick={() => selectedRows.size > 0 ? clearSelection() : selectAll()}
            style={{ padding: '0.4rem 0.75rem', border: '1px solid #e5e7eb', borderRadius: '0.5rem', background: '#fff', cursor: 'pointer' }}
          >
            {selectedRows.size > 0 ? `Unselect (${selectedRows.size})` : `Select All (${rows.filter(r => r.status !== 'completed' && r.status !== 'running').length})`}
          </button>
          
          {selectedRows.size > 0 && (
            <button
              onClick={() => runBulkExecutionSequential(Array.from(selectedRows))}
              disabled={isSequentialRunning || anyRowRunning}
              style={{ 
                padding: '0.4rem 0.75rem', 
                border: '1px solid #e5e7eb', 
                borderRadius: '0.5rem', 
                background: isSequentialRunning || anyRowRunning ? '#e5e7eb' : '#fff', 
                color: isSequentialRunning || anyRowRunning ? '#9ca3af' : '#111827',
                cursor: isSequentialRunning || anyRowRunning ? 'not-allowed' : 'pointer' 
              }}
            >
              Run Selected ({selectedRows.size})
            </button>
          )}
          {isSequentialRunning ? (
            <button 
              onClick={() => { 
                stopSequentialRef.current = true; 
                if (abortControllerRef.current) {
                  abortControllerRef.current.abort();
                }
              }} 
              style={{ padding: '0.4rem 0.75rem', border: '1px solid #ee4444', borderRadius: '0.5rem', background: '#ee4444', color: '#fff', cursor: 'pointer' }}
            >
              Stop
            </button>
          ) : sequentialRemaining.length > 0 ? (
            <button 
              onClick={() => runBulkExecutionSequential(sequentialRemaining)} 
              style={{ padding: '0.4rem 0.75rem', border: '1px solid #3AB981', borderRadius: '0.5rem', background: '#3AB981', color: '#fff', cursor: 'pointer' }}
            >
              Resume ({sequentialRemaining.length})
            </button>
          ) : null}
        </div>

        {/* Table */}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
          {isLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              <Loader2 style={{ width: '2rem', height: '2rem', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
              Loading FAQ data for all trees...
            </div>
          ) : (
            <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
              <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.size > 0 && selectedRows.size === rows.filter(r => r.status !== 'completed' && r.status !== 'running').length}
                        onChange={() => selectedRows.size > 0 ? clearSelection() : selectAll()}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'left', padding: '0.75rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>TREE NAME</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'center', padding: '0.75rem 1.5rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '160px' }}>QUESTIONS</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'center', padding: '0.75rem 1.5rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '160px' }}>EXECUTE</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'center', padding: '0.75rem 1.5rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '160px' }}>DETAILS</th>
                    <th className="sticky-header" style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 1, textAlign: 'center', padding: '0.75rem 1.5rem', fontSize: '0.75rem', color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: '160px' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: '0.875rem' }}>
                  {rows.map(row => (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor: row.status === 'completed' ? '#e5e7eb' : row.status === 'error' ? '#fef2f2' : 'transparent',
                        opacity: row.status === 'completed' ? 0.5 : 1
                      }}
                    >
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                        <input
                          type="checkbox"
                          checked={selectedRows.has(row.id)}
                          onChange={() => toggleRowSelection(row.id)}
                          disabled={row.status === 'running'}
                          style={{ cursor: row.status === 'running' ? 'not-allowed' : 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
                        <div style={{ fontWeight: 500, color: '#111827' }}>{row.treeName}</div>
                        {row.status === 'running' && row.progressInfo && (
                          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            {row.progressInfo}
                          </div>
                        )}
                        {row.status === 'error' && row.progressInfo && (
                          <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.25rem' }}>
                            {row.progressInfo}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#f3f4f6',
                          borderRadius: '0.375rem'
                        }}>
                          <FileText style={{ width: '14px', height: '14px' }} />
                          {row.questionCount}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <button
                          onClick={() => row.status === 'error' ? retryRow(row.id) : executeRow(row.id)}
                          disabled={row.status === 'running' || anyRowRunning || row.questionCount === 0}
                          style={{
                            padding: '0.3125rem 0.625rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #e5e7eb',
                            background: row.status === 'running' ? '#e5e7eb' : (anyRowRunning || row.questionCount === 0 ? '#e5e7eb' : '#ffffff'),
                            color: row.status === 'running' ? '#111827' : (anyRowRunning || row.questionCount === 0 ? '#9ca3af' : (row.status === 'error' ? '#dc2626' : '#111827')),
                            cursor: row.status === 'running' || anyRowRunning || row.questionCount === 0 ? 'not-allowed' : 'pointer',
                            fontSize: 'inherit'
                          }}
                        >
                          {row.status === 'running' ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{
                                display: 'inline-block',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: '#10b981',
                                animation: 'pulse 1.5s ease-in-out infinite'
                              }} />
                              Running
                            </span>
                          ) : row.status === 'error' ? 'Retry' : row.status === 'completed' ? 'Re-run' : 'Run'}
                        </button>
                      </td>
                      <td style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <button
                          onClick={(e) => openDetailsPopup(e, row.id)}
                          disabled={!row.output || row.status === 'running'}
                          style={{
                            padding: '0.3125rem 0.625rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #e5e7eb',
                            background: row.output && row.status !== 'running' ? '#ffffff' : '#e5e7eb',
                            color: row.output && row.status !== 'running' ? '#111827' : '#9ca3af',
                            cursor: row.output && row.status !== 'running' ? 'pointer' : 'not-allowed',
                            fontSize: 'inherit'
                          }}
                          title={row.status === 'running' ? 'Please wait for generation to complete' : row.output ? 'Show backend workflow details' : 'No details available yet'}
                        >
                          Show
                        </button>
                      </td>
                      <td style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid #f3f4f6', textAlign: 'center' }}>
                        <button
                          onClick={() => openActionModal(row.id)}
                          disabled={!row.output || row.status === 'running'}
                          style={{
                            padding: '0.3125rem 0.625rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #e5e7eb',
                            background: row.output && row.status !== 'running' ? '#EBF212' : '#e5e7eb',
                            color: row.output && row.status !== 'running' ? '#000000' : '#9ca3af',
                            cursor: row.output && row.status !== 'running' ? 'pointer' : 'not-allowed',
                            fontSize: 'inherit'
                          }}
                        >
                          See Output
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal */}
      {isActionModalOpen && activeModalRowId && (
        <PAAContentActionModal
          isOpen={isActionModalOpen}
          onClose={closeActionModal}
          row={(() => {
            const r = rows.find(row => row.id === activeModalRowId);
            if (!r) return null as any;
            return {
              id: r.id,
              question: r.treeName, // Use tree name as title
              treeName: r.treeName,
              treeSlug: r.treeSlug,
              status: r.status,
              output: r.output,
              seoTitle: r.seoTitle,
              seoDescription: r.seoDescription,
              metadata: r.metadata,
              createdAt: r.createdAt
            };
          })()}
          brandKit={null}
          onContentUpdate={handleModalContentUpdate}
        />
      )}

      {/* Backend Details Popup */}
      {popupState.isVisible && popupState.rowId && (
        <BackendDetailsPopup
          workflowDetails={rows.find(r => r.id === popupState.rowId)?.workflowDetails as any || {}}
          status={rows.find(r => r.id === popupState.rowId)?.status === 'running' ? 'running' : 'completed'}
          isVisible={true}
          position={popupState.position}
          onMouseEnter={() => {}}
          onMouseLeave={closeDetailsPopup}
          onMobileClose={closeDetailsPopup}
          dismissBehavior="manual"
          generatedContent={rows.find(r => r.id === popupState.rowId)?.output}
        />
      )}

      {/* Local styles for sticky header */}
      <style>
        {`
          .sticky-header { position: sticky; top: 0; }
        `}
      </style>
    </>
  );
};

export default PeopleAlsoAskPage;
